import type { CsViewType } from "@/service/course.service";
import type { CourseModel, UpdateScormStatus } from "@/types/course.model";

type LinkedCourseSource = Pick<
  CourseModel,
  "id" | "grade_id" | "course_id" | "grade"
> | null | undefined;

/**
 * Linked LMS course uuid for class SCORM webhook `data.grade`.
 * GET /cs/api/v1/view/:classOfferingId?type=class returns it as `data.id`
 * (no grade_id / course_id / grade object on the response).
 */
export function resolveLinkedCourseGradeId(
  courseData: LinkedCourseSource,
  viewType: CsViewType,
  fallbackGradeId?: string
): string | undefined {
  const fromFallback = fallbackGradeId?.trim();
  if (fromFallback) return fromFallback;

  if (!courseData) return undefined;
  if (courseData.grade_id?.trim()) return courseData.grade_id.trim();
  if (courseData.course_id?.trim()) return courseData.course_id.trim();
  if (courseData.grade?.id?.trim()) return courseData.grade.id.trim();
  if (viewType === "class" && courseData.id?.trim()) {
    return courseData.id.trim();
  }

  return undefined;
}

export function buildScormUpdateStatusPayload(params: {
  viewType: CsViewType;
  /** Route id: course/grade uuid (course) or class offering uuid (class) */
  resourceId: string;
  /** GET /cs/api/v1/view/:id?type=class — linked LMS course uuid */
  linkedCourseId?: string;
  lessonId: string;
  unitId: string;
  userId: string;
  status?: "COMPLETED" | "PENDING";
}): UpdateScormStatus | null {
  const {
    viewType,
    resourceId,
    linkedCourseId,
    lessonId,
    unitId,
    userId,
    status = "COMPLETED",
  } = params;

  if (viewType === "class") {
    const grade = linkedCourseId?.trim();
    if (!grade) {
      console.error(
        "SCORM webhook: missing linked course id for class view (expected data.id from CS view API)"
      );
      return null;
    }

    return {
      func: "scorm_update_status",
      data: {
        user: userId,
        grade,
        lesson: lessonId,
        unit: unitId,
        status,
        type: "class",
        class_id: resourceId,
      },
    };
  }

  return {
    func: "scorm_update_status",
    data: {
      grade: resourceId,
      lesson: lessonId,
      unit: unitId,
      user: userId,
      duration: "",
      status,
    },
  };
}

/** Resource id + view type for refetching counts after webhook. */
export function getScormRefetchParams(payload: UpdateScormStatus): {
  gradeId: string;
  viewType: CsViewType;
} {
  if ("type" in payload.data && payload.data.type === "class") {
    return { gradeId: payload.data.class_id, viewType: "class" };
  }

  return { gradeId: payload.data.grade, viewType: "course" };
}
